import React, { useEffect, useState } from 'react';
import Button from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useAppConfig } from '@/contexts/AppConfigContext.jsx';
import { billingAPI } from '@/services/api';
import { trackOnce } from '@/services/analytics';
import Tooltip from '@/components/common/Tooltip';

const Feature = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="mt-1 h-2 w-2 rounded-full bg-indigo-600" />
    <span className="text-gray-700 text-sm">{children}</span>
  </li>
);

const SubscribePage = () => {
  const { currentUser, isPremium } = useAuth();
  const { premiumCopy } = useAppConfig();
  const trialDays = premiumCopy.trialDays;
  const [checking, setChecking] = useState(false);
  const [freshPremium, setFreshPremium] = useState(isPremium);
  const [mockMode, setMockMode] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const planDescription = premiumCopy.description;

  useEffect(() => {
    let mounted = true;
    if (currentUser) {
      setChecking(true);
      billingAPI.getStatus().then((r) => {
        if (!mounted) return;
        if (r?.premium) setFreshPremium(true);
        setTrialInfo(r);
        if (r?.trialEnd && !r.premium) {
          const rawEnd = Number(r.trialEnd);
          const effectiveEnd = Number(r.effectiveTrialEnd || rawEnd);
          const nowTs = r.now || Date.now();
          // Fire trialWarning when <=3 days left (once per 24h)
          const remainMs = effectiveEnd - nowTs;
          if (remainMs > 0) {
            const days = Math.ceil(remainMs / 86400000);
            if (days <= 3) {
              trackOnce('trialWarning', 'trialWarning', { daysRemaining: days }, 24*60*60*1000);
            }
            // Grace start detection
            if (effectiveEnd > rawEnd && nowTs >= rawEnd && nowTs < effectiveEnd) {
              trackOnce('graceStart', 'graceStart', { msRemaining: remainMs }, 24*60*60*1000);
            }
          } else if (nowTs >= effectiveEnd) {
            // Past effective end -> trialEnd event once
            trackOnce('trialEnd', 'trialEnd', { trialEnd: rawEnd }, 7*24*60*60*1000);
          }
        }
      }).catch(() => {}).finally(() => mounted && setChecking(false));
    }
    return () => { mounted = false; };
  }, [currentUser]);

  const onSubscribe = async () => {
    try {
      const status = await billingAPI.getStatus().catch(() => null);
      if (status?.premium) {
        window.location.href = '/premium';
        return;
      }
      const { url, mock } = await billingAPI.createCheckoutSession();
      if (mock) setMockMode(true);
      if (url) window.location.assign(url);
    } catch (e) {
      console.error(e);
    }
  };

  const onManage = async () => {
    try {
      const { url, mock } = await billingAPI.createPortalSession();
      if (mock) setMockMode(true);
      if (url) window.location.assign(url);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-card border border-border shadow-sm rounded-lg p-6">
          <h1 className="text-2xl font-bold mb-2">Premium Subscription</h1>
          <p className="text-muted-foreground mb-6">
            Upgrade to unlock the financial planner: budgets, categories, goals, and insights.
            Enjoy a {trialDays}-day full-access trial, then just {premiumCopy.pricePerUserPerMonth}.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">What you get</h2>
              <ul className="space-y-2">
                <Feature>Budgets with monthly limits</Feature>
                <Feature>Custom categories with rollups</Feature>
                <Feature>Goals and progress tracking</Feature>
                <Feature>Import transactions (CSV)</Feature>
                <Feature>Charts and insights</Feature>
                <Feature>Priority support</Feature>
                {mockMode && <Feature>Mock billing enabled (dev)</Feature>}
              </ul>
            </div>
            <div className="rounded-lg p-4 bg-background border border-border">
              <div className="text-sm text-muted-foreground">Plan</div>
              <div className="text-xl font-semibold">Premium</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-4xl font-bold">{premiumCopy.priceDisplay}</span>
                <span className="text-sm text-muted-foreground">per user / month</span>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">{planDescription}</div>
              <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{premiumCopy.trialCopy}</div>
              <div className="mt-6 space-y-3">
                {currentUser ? (
                  freshPremium ? (
                    <Tooltip label="Update your billing details or cancel your subscription safely." className="block w-full">
                      <Button variant="outline" onClick={onManage} disabled={checking} loading={checking}>Manage Subscription</Button>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Get full analytics, unlimited lists, and advanced finance insights." className="block w-full">
                      <Button onClick={onSubscribe} disabled={checking} loading={checking}>Subscribe</Button>
                    </Tooltip>
                  )
                ) : (
                  <a
                    href="/auth/signin"
                    className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-primary-foreground hover:opacity-90"
                  >
                    Sign in to subscribe
                  </a>
                )}
                {trialInfo && (trialInfo.trialEnd || trialInfo.effectiveTrialEnd) && !freshPremium && (() => {
                  const rawEnd = Number(trialInfo.trialEnd) || 0;
                  const effectiveEnd = Number(trialInfo.effectiveTrialEnd || rawEnd);
                  const nowTs = trialInfo.now || Date.now();
                  const inGrace = effectiveEnd > rawEnd && nowTs >= rawEnd && nowTs < effectiveEnd;
                  const remainMs = effectiveEnd - nowTs;
                  if (remainMs > 0) {
                    const days = Math.ceil(remainMs / 86400000);
                    const baseClass = days <= 3 ? 'text-yellow-700 bg-yellow-50 border border-yellow-200' : 'text-green-700 bg-green-50 border border-green-200';
                    return (
                      <div className={`text-xs font-medium ${baseClass} rounded px-2 py-1`}>
                        {inGrace ? 'Grace period: ' : 'Free trial: '}{days} day{days !== 1 ? 's' : ''} remaining of full access
                      </div>
                    );
                  }
                  return null;
                })()}
                {trialInfo?.cancelAtPeriodEnd && (
                  <div className="text-xs text-muted-foreground">You have canceled. Access ends at end of trial.</div>
                )}
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">Payments are handled securely by Stripe. You can cancel any time from the customer portal.</p>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
